import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { AppView, CardView, rem, ScrollView } from 'design-system';
import { IconBoldCalendar, IconCoinStack, IconDownload, IconMenuBold, IconMoneyBills, } from 'design-system/icons';
import Text from 'design-system/Text';
import StatusBar from 'design-system/StatusBar';
import Button, { HEIGHT } from 'design-system/Button';
import { IconUpload } from 'design-system/icons/IconUpload';
import IconBoldSaved from 'design-system/icons/IconBoldSaved';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Amplitude from 'utils/amplitude';
import { applyToSwitching } from 'actions';
import { formatAmount } from 'utils/formatting';
import useHeaderHeight from 'hooks/useHeaderHeight';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch } from 'store/hooks';
import Row from '../Row';
import Header from '../Header';
const headerTopHeight = rem(216);
const shadowOpacityOutputRange = [0, 0, 1, 1];
const BroadbandQuoteScreen = ({ route, navigation, }) => {
    const { data, enquiryId } = route.params;
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const scrollY = useSharedValue(0);
    useEffect(() => {
        Amplitude.logEvent('Broadband.Quote');
    }, []);
    const onSwitchNow = useCallback(() => {
        Promise.resolve(dispatch(applyToSwitching(enquiryId, data.id))).then((response) => {
            if (response &&
                response.payload &&
                response.payload.webview &&
                response.payload.webview.url) {
                Links.openLink(response.payload.webview.url);
                Amplitude.logEvent('Broadband.RedirectToDeal');
            }
            return response;
        });
    }, [data.id, enquiryId]);
    const onSwitchNowCenter = useCallback(() => {
        onSwitchNow();
        Amplitude.logEvent('Broadband.QuoteClickVisitNow', { position: 'center' });
    }, [onSwitchNow]);
    const onSwitchNowBottom = useCallback(() => {
        onSwitchNow();
        Amplitude.logEvent('Broadband.QuoteClickVisitNow', { position: 'bottom' });
    }, [onSwitchNow]);
    const { name, provider, costs, contractLengthInMonths, broadband, features, homephone, discountedOffers, } = data.data;
    const { averageMonthlyCost, totalUpfrontCost } = costs;
    const broadbandSection = useMemo(() => {
        if (!broadband)
            return null;
        const { averageDownloadSpeedKbps, averageUploadSpeedKbps } = broadband;
        let downloadSpeed = averageDownloadSpeedKbps / 1000;
        downloadSpeed = Math.round(downloadSpeed * 10) / 10;
        let uploadSpeed = averageUploadSpeedKbps / 1000;
        uploadSpeed = Math.round(uploadSpeed * 10) / 10;
        return (<CardView noMarginTop>
        <View style={styles.headerContainer}>
          <Text Text-16>Broadband</Text>
        </View>
        <Row title="Download Speed" value={`${downloadSpeed}MB`} Icon={IconDownload} alt gradientKey="skyBlue"/>
        <Row title="Upload Speed" value={`${uploadSpeed}MB`} Icon={IconUpload} gradientKey="lightPink"/>
      </CardView>);
    }, [broadband, styles]);
    const featuresSection = useMemo(() => {
        const rows = [];
        if (features) {
            for (let index = 0; index < features.length; index++) {
                if (features[index]) {
                    rows.push(<Row key={features[index]} title={features[index]} Icon={IconBoldSaved}/>);
                }
            }
            return (<CardView noMarginTop>
          <View style={styles.headerContainer}>
            <Text Text-16>Features</Text>
          </View>
          {rows}
        </CardView>);
        }
        return null;
    }, [features, styles]);
    const phoneSection = useMemo(() => {
        if (!homephone)
            return null;
        return (<CardView noMarginTop>
        <View style={styles.headerContainer}>
          <Text Text-16>Phone</Text>
        </View>
        <Row title="Plan" value={homephone.description} Icon={IconMenuBold}/>
      </CardView>);
    }, [homephone, styles]);
    const headerHeight = useHeaderHeight();
    const animationInputRange = useMemo(() => [
        0,
        headerTopHeight - headerHeight,
        headerTopHeight,
        headerTopHeight + 1,
    ], [headerHeight]);
    const bgColorOutputRange = useMemo(() => [
        colors.background.dark,
        colors.background.dark,
        colors.cards.onDark,
        colors.cards.onDark,
    ], [colors.background.dark, colors.cards.onDark]);
    const paddingBottom = useMarginBottom('paddingBottom', rem(8) + HEIGHT);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <ScrollView secondary scrollY={scrollY} bgColorOutputRange={bgColorOutputRange} animationInputRange={animationInputRange} contentContainerStyle={paddingBottom} shadowOpacityOutputRange={shadowOpacityOutputRange} expandedHeaderHeight={headerHeight + headerTopHeight}>
        <CardView noMarginTop>
          <View style={styles.headerContainer}>
            <Text Text-16>Costs</Text>
          </View>
          <Row title="Monthly Cost" value={formatAmount(averageMonthlyCost, 'GBP')} Icon={IconCoinStack} gradientKey="ultimate"/>
          <Row title="Contract Length" value={`${contractLengthInMonths} months`} Icon={IconBoldCalendar} gradientKey="apricot"/>
          {Boolean(totalUpfrontCost) && (<Row title="Setup Costs" value={formatAmount(totalUpfrontCost, 'GBP')} Icon={IconMoneyBills} color={colors.elements.negative}/>)}
        </CardView>
        {broadbandSection}
        {phoneSection}
        {featuresSection}
      </ScrollView>
      <Header logoUrl={provider.logo} scrollY={scrollY} title={provider.name} subtitle={name} discountedOffers={discountedOffers} onPress={onSwitchNowCenter}/>
      <Button floating brand onPress={onSwitchNowBottom} title="Go to website"/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    paddingHorizontal35: { paddingHorizontal: rem(35) },
    headerContainer: {
        padding: rem(16),
        borderBottomWidth: rem(1),
        borderBottomColor: colors.background.dark,
    },
    title: {
        color: 'white',
        textAlign: 'center',
        paddingLeft: 20,
        paddingRight: 20,
    },
}));
export default BroadbandQuoteScreen;
